import React, { useCallback, useRef, useState } from "react";
import { SelectBox } from "devextreme-react/select-box";
import { Button } from "devextreme-react/button";
import { Popup } from "devextreme-react/popup";
import DataSource from "devextreme/data/data_source";
import CustomStore from "devextreme/data/custom_store";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { MESSAGES } from "./Messages";

// Generic function to load data into a SelectBox
export const LoadDataSource = ({ loadUrl, key, loadParams, accessToken,sort, pageSize = 10, resolveKeyLocally }) => {
    const store = createStore({
        loadUrl: loadUrl,
        key: key,
        loadParams: loadParams,
        onBeforeSend: function (method, ajaxOptions) {
            ajaxOptions.headers = {
                Authorization: `Bearer ${accessToken}`,
            };
        },
    });
    return new DataSource({
        // The already-picked value is often not on the first loaded page (item / lot pickers are
        // paginated). resolveKeyLocally lets the caller answer byKey from the row it already has,
        // so the cell keeps showing its code instead of going blank while editing.
        store: resolveKeyLocally
            ? new CustomStore({
                key: key,
                load: (loadOptions) => store.load(loadOptions),
                byKey: (itemKey) => {
                    const localItem = resolveKeyLocally(itemKey);
                    return localItem ? Promise.resolve(localItem) : store.byKey(itemKey);
                },
            })
            : store,
        sort:sort,
        paginate: true,
        pageSize: pageSize,
    });
};

/**
 * Hosts a detail component for the cell's current value.
 *
 * Two shapes of detail component are supported:
 *  - `hostsOwnPopup` (default): the component renders its own <Popup> and follows the project's
 *    showPopup / setShowPopup contract (ImItemStatusPopup, ItemPricesPopup, …).
 *  - otherwise: plain content, hosted in a <Popup> here (a list, a grid, …).
 */
export const DetailPopupHost = ({
    component: DetailComponent,
    value,
    componentProps,
    hostsOwnPopup = true,
    title,
    width = "80vw",
    height = "80vh",
    onClose,
}) => {
    const [showPopup, setShowPopup] = useState({ visible: true, ...(value || {}) });
    // The hosted component closes itself by pushing visible: false; unmounting it is the close.
    const handleSetShowPopup = useCallback((next) => {
        const resolved = typeof next === "function" ? next(showPopup) : next;
        if (resolved?.visible === false) {
            if (onClose) onClose();
            return;
        }
        setShowPopup(resolved);
    }, [showPopup, onClose]);

    if (!DetailComponent) return null;
    if (!hostsOwnPopup) {
        return (
            <Popup
                visible={true}
                onHiding={onClose}
                showTitle={true}
                title={title}
                width={width}
                height={height}
                dragEnabled={true}
                showCloseButton={true}
                hideOnOutsideClick={false}
            >
                <DetailComponent {...(value || {})} {...componentProps} />
            </Popup>
        );
    }
    return (
        <DetailComponent showPopup={showPopup} setShowPopup={handleSetShowPopup} {...componentProps} />
    );
};

/**
 * Keeps a cell's detail popup outside the cell that opened it.
 *
 * A DataGrid closes (and unmounts) the edit cell as soon as focus leaves it, which would take a
 * popup rendered inside `editCellRender` with it. The parent renders `detailPopup` next to its
 * other popups and passes `openDetailPopup` as the cell's `detail.onOpen`.
 */
export const useDetailPopup = () => {
    const [detail, setDetail] = useState(null);
    const openCountRef = useRef(0);
    const openDetailPopup = useCallback((config) => {
        openCountRef.current += 1;
        setDetail({ ...config, openId: openCountRef.current });
    }, []);
    const closeDetailPopup = useCallback(() => setDetail(null), []);
    const detailPopup = detail ? (
        <DetailPopupHost key={detail.openId} {...detail} onClose={closeDetailPopup} />
    ) : null;
    return { openDetailPopup, closeDetailPopup, detailPopup };
};

/**
 * The legacy desktop lookup glyph beside a field (IM Lot Code Entry → Class / Sub Class): opens
 * `component` in a popup, scoped to `value` (whatever the detail component needs from the row).
 *
 * Inside a DataGrid pass `onOpen` from useDetailPopup so the popup survives the editor closing.
 * Without `onOpen` the button hosts the popup itself, which is fine outside grids.
 */
export const CellDetailButton = ({
    component,
    value,
    componentProps,
    hostsOwnPopup = true,
    title,
    width = "80vw",
    height = "80vh",
    icon = "edit",
    hint = MESSAGES.CELL_DETAIL_LOOKUP_HINT,
    disabled = false,
    onOpen,
}) => {
    const [localDetail, setLocalDetail] = useState(null);
    const openDetail = useCallback(() => {
        const config = { component, value, componentProps, hostsOwnPopup, title, width, height };
        if (onOpen) onOpen(config);
        else setLocalDetail(config);
    }, [component, value, componentProps, hostsOwnPopup, title, width, height, onOpen]);

    if (!component) return null;
    return (
        <>
            <Button
                icon={icon}
                hint={hint}
                disabled={disabled}
                stylingMode="text"
                onClick={openDetail}
            />
            {localDetail && (
                <DetailPopupHost {...localDetail} onClose={() => setLocalDetail(null)} />
            )}
        </>
    );
};

// Modified RenderSelectBoxCell
export const RenderSelectBoxCell = ({
    loadUrl,
    key,
    loadParams,
    valueExpr,
    displayExpr,
    // Defaults to displayExpr, which is right for the usual string displayExpr. A caller that composes
    // its entry text with a function has to name the field to search: the search filter goes to the
    // server, and a function does not survive the trip.
    searchExpr,
    accessToken,
    pageSize = 10,
    defaultValue= "",
    sort,
    onValueChangeCallback,
    acceptCustomValue,
    disabled,
    resolveKeyLocally,
    detail
}) => {
    // Return a function to be used as `editCellRender`
    // No loadUrl means there is nothing to pick from yet (e.g. an item picker before a warehouse
    // is chosen) — render the empty editor rather than firing a request without its scope.
    const dataSource = loadUrl
        ? LoadDataSource({
            loadUrl,
            key,
            loadParams,
            accessToken,
            sort,
            pageSize,
            resolveKeyLocally,
        })
        : undefined;

    const selectBox = (
        <SelectBox
            dataSource={dataSource}
            valueExpr={valueExpr}
            displayExpr={displayExpr}
            searchEnabled={true}
            searchExpr={searchExpr}
            searchMode="contains"
            defaultValue={defaultValue}
            acceptCustomValue={acceptCustomValue}
            showClearButton={true}
            disabled={disabled}
            onValueChanged={(e) => {
                const selectedText = e.component.option("displayValue");
                const additionalData = e.component.option("selectedItem");
                if (onValueChangeCallback) {
                    onValueChangeCallback(e.value, selectedText,additionalData); // Pass value and text to the callback
                }
            }}
            onCustomItemCreating={(e) => {
                if (e.text && acceptCustomValue) {
                    e.customItem = e.text; // Set the custom item as the selected value
                    if (onValueChangeCallback) {
                        onValueChangeCallback(e.text, e.text, null); // Pass custom value
                    }
                }
            }}
        />
    );

    if (!detail?.component) return selectBox;
    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ flex: "1 1 auto", minWidth: 0 }}>{selectBox}</div>
            <CellDetailButton {...detail} />
        </div>
    );
};
export default RenderSelectBoxCell;
